import React, { useMemo, useState } from 'react';
import { useDashboard } from '@/context/DashboardContext';
import { PROGRAM_LIST } from '@/data/seed';
import { cr, crFull, fmtDate, daysFrom, shortIN, utilColor } from '@/lib/fmt';
import KpiCard from '@/components/KpiCard';
import { Chip, ProgramChip, ProgressBar } from '@/components/UiBits';
import { FILTERS, DONOR } from '@/constants/testIds';
import { Users, TrendingUp, Target, Clock, Shield, RotateCcw, Pencil, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const empty = { name: '', type: 'CSR', fcra: false, since: 2024, programs: ['education'], renewal: 'Multi-year', states: ['Delhi NCR'] };

export default function Donors() {
  const { donors, grants, addDonor, updateDonor, removeDonor, TODAY, PROGRAMS: P, REGIONS } = useDashboard();
  const [t, setT] = useState('');
  const [pw, setPw] = useState('');
  const [rn, setRn] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const active = [t, pw, rn, q].filter(Boolean).length;
  const reset = () => { setT(''); setPw(''); setRn(''); setQ(''); };

  const totals = useMemo(() => ({
    sanct: grants.reduce((s, g) => s + g.sanct, 0),
  }), [grants]);

  const donorCommitted = (d) => grants.filter((g) => g.donor === d.id).reduce((s, g) => s + g.sanct, 0);
  const donorsSorted = [...donors].sort((a, b) => donorCommitted(b) - donorCommitted(a));
  const top3Share = donorsSorted.slice(0, 3).reduce((s, d) => s + donorCommitted(d), 0) / (totals.sanct || 1) * 100;
  const avgRel = donors.length ? donors.reduce((s, d) => s + (2026 - d.since), 0) / donors.length : 0;

  const list = donorsSorted.filter((d) =>
    (!t || d.type === t) &&
    (!pw || d.programs.includes(pw)) &&
    (!rn || d.renewal === rn) &&
    (!q || d.name.toLowerCase().includes(q.toLowerCase()))
  );

  const openAdd = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (d) => { setEditingId(d.id); setForm({ ...d }); setOpen(true); };
  const save = () => {
    if (!form.name.trim()) { toast.error('Donor name is required'); return; }
    if (!form.programs || form.programs.length === 0) { toast.error('Select at least one program'); return; }
    if (editingId) { updateDonor(editingId, form); toast.success('Donor updated'); }
    else { addDonor(form); toast.success('Donor added'); }
    setOpen(false);
  };
  const del = (d) => { removeDonor(d.id); toast.success(`Removed ${d.name}`); };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Relationships</div>
          <h1>Donor Portfolio</h1>
          <div className="sub">Every active funder — commitment, utilisation, obligations and renewal posture</div>
        </div>
        <Button
          type="button"
          data-testid={DONOR.add}
          onClick={openAdd}
          className="gap-2 rounded-lg font-medium"
          style={{ background: 'var(--llf-gold)', color: 'var(--llf-ink)' }}
        >
          <Plus size={14} /> Add donor
        </Button>
      </div>

      <div className="filter-strip">
        <div className="filter-group">
          <label>Donor Type</label>
          <select value={t} onChange={(e) => setT(e.target.value)}>
            <option value="">All types</option>
            {['CSR','Foundation','FCRA','PSU Fdn'].map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Program</label>
          <select value={pw} onChange={(e) => setPw(e.target.value)}>
            <option value="">All programs</option>
            {PROGRAM_LIST.map((k) => <option key={k} value={k}>{P[k].name}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Renewal Status</label>
          <select value={rn} onChange={(e) => setRn(e.target.value)}>
            <option value="">All</option>
            {['Multi-year','Renewal due','At risk'].map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input type="search" placeholder="Search donor…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {active > 0 && (
          <button className="btn-reset" onClick={reset} style={{ display: 'inline-flex' }}>
            <RotateCcw size={13} /> Reset Filters <span className="badge-count">{active}</span>
          </button>
        )}
      </div>

      <div className="kpi-row">
        <KpiCard icon={Users}      iconCls="ic-gold"  value={donors.length}     label="Active Donors"        delta={['up','2 added this FY']} />
        <KpiCard icon={Users}      iconCls="ic-blue"  value={2}                 label="New Donors FY 25-26"  delta={['up','Aristocrat · HDB']} />
        <KpiCard icon={Target}     iconCls="ic-green" value="86%"               label="Donor Retention"      delta={['up','vs 81% last FY']} />
        <KpiCard icon={Clock}      iconCls="ic-amber" value={avgRel.toFixed(1) + ' yrs'} label="Avg. Relationship"   delta={['up','longest: Bosch, 11 yrs']} />
        <KpiCard icon={Shield}     iconCls="ic-red"   value={top3Share.toFixed(1) + '%'} label="Top-3 Concentration"  delta={['down','target < 50%']} />
      </div>

      <div className="donor-grid">
        {list.map((d) => {
          const gs = grants.filter((g) => g.donor === d.id);
          const committed = gs.reduce((s, g) => s + g.sanct, 0);
          const recd = gs.reduce((s, g) => s + g.recd, 0);
          const util = gs.reduce((s, g) => s + g.util, 0);
          const nxt = [...gs].sort((a, b) => new Date(a.report) - new Date(b.report))[0];
          const initials = d.name.split(' ').slice(0, 2).map((w) => w[0]).join('');
          const overdue = gs.some((g) => g.uc === 'Overdue');
          const utilPct = recd ? (util / recd) * 100 : 0;
          return (
            <div key={d.id} data-testid={DONOR.card(d.id)} className="donor-card">
              <div className="dc-head">
                <div className="dc-logo">{initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="dc-name">{d.name}</div>
                  <div className="dc-since">Partner since {d.since} · {2026 - d.since} yrs</div>
                </div>
                <Chip kind={d.type}>{d.type}</Chip>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {d.programs.map((k) => <ProgramChip key={k} program={k} PROGRAMS={P} />)}
                {d.fcra && d.type !== 'FCRA' && <Chip kind="FCRA">FCRA</Chip>}
              </div>
              <div className="dc-stats">
                <div className="dc-stat"><div className="v" title={crFull(committed)}>{cr(committed)}</div><div className="l">Committed</div></div>
                <div className="dc-stat"><div className="v" title={crFull(recd)}>{cr(recd)}</div><div className="l">Received</div></div>
                <div className="dc-stat"><div className="v">{gs.length}</div><div className="l">Grants</div></div>
                <div className="dc-stat"><div className="v">{shortIN(gs.reduce((s, g) => s + g.benef, 0))}</div><div className="l">Beneficiaries</div></div>
              </div>
              <div>
                <div className="flex justify-between text-[11px] text-[color:var(--gray-500)] mb-1">
                  <span>Utilisation vs received</span>
                  <span>{cr(util)} of {cr(recd)}</span>
                </div>
                <ProgressBar pct={utilPct} color={utilColor(utilPct)} />
              </div>
              <div className="dc-foot">
                <Chip kind={d.renewal}>{d.renewal}</Chip>
                {overdue ? <Chip kind="Overdue">Report overdue</Chip> :
                  nxt ? <Chip kind={daysFrom(nxt.report, TODAY) <= 15 ? 'Due' : 'closed'}>Next report {fmtDate(nxt.report)}</Chip> : null}
                <Chip kind="closed">{d.states.length} states</Chip>
                <div className="ml-auto flex gap-1 opacity-80 hover:opacity-100 transition-opacity">
                  <button data-testid={DONOR.edit(d.id)} onClick={() => openEdit(d)} aria-label="Edit donor"
                    className="w-7 h-7 grid place-items-center rounded border border-[color:var(--gray-200)] bg-white hover:bg-[color:var(--gray-100)]">
                    <Pencil size={12} />
                  </button>
                  <button data-testid={DONOR.delete(d.id)} onClick={() => del(d)} aria-label="Delete donor"
                    className="w-7 h-7 grid place-items-center rounded border border-[color:var(--gray-200)] bg-white hover:bg-[color:var(--red-100)] hover:text-[color:var(--red-700)]">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="empty-state col-span-full">
            <div className="empty-title">No donors match filters</div>
            <div className="empty-sub">Try clearing a filter or two.</div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <div className="card-title">Concentration Detail</div>
            <div className="card-sub">Committed ₹ and share of active portfolio</div>
          </div>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>#</th><th>Donor</th><th>Type</th><th className="num">Committed</th><th className="num">Share</th><th className="num">Cumulative</th></tr>
            </thead>
            <tbody>
              {(() => {
                let cum = 0;
                return donorsSorted.map((d, i) => {
                  const v = donorCommitted(d);
                  const share = (v / (totals.sanct || 1)) * 100;
                  cum += share;
                  return (
                    <tr key={d.id}>
                      <td style={{ color: 'var(--gray-400)' }}>{i + 1}</td>
                      <td><span className="ent">{d.name}</span></td>
                      <td><Chip kind={d.type}>{d.type}</Chip></td>
                      <td className="num" title={crFull(v)}>{cr(v)}</td>
                      <td className="num">{share.toFixed(1)}%</td>
                      <td className="num" style={{ color: cum > 75 && i < 4 ? 'var(--red-600)' : 'var(--gray-500)' }}>
                        {cum.toFixed(1)}%
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[color:var(--gray-900)]">
              {editingId ? 'Edit donor' : 'Add a donor'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Donor name">
              <Input data-testid={DONOR.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Tata Trusts" className="rounded-lg" />
            </Field>
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid={DONOR.type} className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['CSR','Foundation','FCRA','PSU Fdn'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Primary program">
              <Select value={form.programs[0]} onValueChange={(v) => setForm({ ...form, programs: [v] })}>
                <SelectTrigger data-testid={DONOR.program} className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROGRAM_LIST.map((k) => <SelectItem key={k} value={k}>{P[k].name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Renewal status">
              <Select value={form.renewal} onValueChange={(v) => setForm({ ...form, renewal: v })}>
                <SelectTrigger data-testid={DONOR.status} className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Multi-year','Renewal due','At risk'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Partner since (year)">
              <Input type="number" min="2005" max="2026" value={form.since}
                onChange={(e) => setForm({ ...form, since: parseInt(e.target.value, 10) || 2024 })}
                className="rounded-lg" />
            </Field>
            <Field label="Primary state / region">
              <Select value={form.states[0]} onValueChange={(v) => setForm({ ...form, states: [v] })}>
                <SelectTrigger data-testid={DONOR.region} className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" data-testid={DONOR.cancel} onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={DONOR.save} onClick={save}
              style={{ background: 'var(--llf-gold)', color: 'var(--llf-ink)' }}>
              {editingId ? 'Save changes' : 'Add donor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-[color:var(--gray-500)] font-semibold">{label}</Label>
    {children}
  </div>
);
